import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container, SearchInput } from '../../../components';
import { useProposal } from '../../../context/proposalContext/ProposalContextProvider';
import Table from '../../../components/common/table/Table';
import ProposalStatus from './ProposalStatus';
import { FaFilePdf, FaTrash } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import { PiMicrosoftExcelLogoLight } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import StatusFilterBar from './StatusFilterBar'; // Import the filter component
import Comment from '../comment/Comment';
import ActionButtons from '../../../components/common/ActionButtons';
import { ImTable2 } from 'react-icons/im';

const ProposalTable = () => {
  const {
    getQuote,
    proposalData,
    deleteQuote,
    totalElements,
    currentPage,
    setCurrentPage,
    pageSize,
    stateFilter,
    setSearchQuery,
    setStateFilter,
    searchQuery,
    updateStates,
    getproposalPdf,
    getExcelById,
    exportToPronto,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
    isLoading, // Add isLoading from context
  } = useProposal();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]); // Store selected proposal IDs
  const [showModal, setShowModal] = useState(false);
  const sampleProposal = {
    id: 'SP201282',
    date: '01-11-2024',
    customerName: 'AXIOM COLLEGE',
    supplier: 'WINC',
    salesPerson: 'Leanne McCulloch',
  };

  const fetchQuotes = useCallback(() => {
    getQuote();
  }, [stateFilter, currentPage, searchQuery]);

  useEffect(() => {
    // Debounce API call to avoid multiple calls in quick succession
    const timer = setTimeout(() => {
      fetchQuotes();
    }, 300); // Adjust debounce time as needed

    return () => clearTimeout(timer); // Clean up the timeout on dependency change
  }, [fetchQuotes]);

  // Reset `currentPage` to 0 when `stateFilter` changes
  useEffect(() => {
    setCurrentPage(0);
  }, [stateFilter, searchQuery]);

  // Handle edit button click

  const handleEdit = (proposalId) => {
    navigate(`/createquote/${proposalId}`);
  };

  // Handle checkbox selection
  const handleCheckboxChange = (proposalId) => {
    setSelectedIds((prevSelected) =>
      prevSelected.includes(proposalId)
        ? prevSelected.filter((id) => id !== proposalId)
        : [...prevSelected, proposalId],
    );
  };

  // Button to mark proposals as WON
  const handleMarkWon = () => {
    updateStates({ quoteIds: selectedIds, newState: 'WON' });
    setSelectedIds([]);
  };

  const handleSortChange = ({ field, direction }) => {
    setSortBy(field); // Update the sortBy field
    setSortDir(direction); // Update the sort direction
    getQuote(currentPage, pageSize, searchQuery, stateFilter, field, direction); // Fetch sorted data
  };

  // Button to mark proposals as LOST
  const handleMarkLost = () => {
    updateStates({ quoteIds: selectedIds, newState: 'LOST' });
    setSelectedIds([]);
  };

  const columns = [
    {
      header: '',
      render: (rowData) => (
        <div className="flex justify-center">
          <input
            type="checkbox"
            disabled={rowData.state === 'PENDING' || rowData.state === 'SAVED'}
            onChange={() => handleCheckboxChange(rowData.id)}
            checked={selectedIds.includes(rowData.id)}
          />
        </div>
      ),
    },
    { header: 'Quote Code', field: 'quoteCode' },
    { header: 'Created Date', field: 'createdDate' },
    { header: 'Customer Name', field: 'customer.customerName' },
    { header: 'Email', field: 'customer.email' },
    {
      header: 'Sales Person',
      render: (rowData) => (
        <>
          <span>
            {rowData.salesPerson?.firstName} {rowData.salesPerson?.lastName}
          </span>
        </>
      ),
      field: 'salesPerson.firstName',
    },
    { header: 'Supplier', field: 'currentSupplier.supplierName' },
    { header: 'Modified Date', field: 'updatedDate' },
    { header: 'Close Date', field: 'closeDate' },
    {
      header: 'Status',
      field: 'state',
      render: (rowData) => (
        <ProposalStatus
          state={rowData.state}
          updatedDate={rowData.updatedDate}
          closeDate={rowData.closeDate}
        />
      ),
    },
    {
      header: 'ACTIONS',
      render: (rowData) => (
        <ActionButtons
          actions={[
            {
              icon: <MdEdit />,
              onClick: () => handleEdit(rowData.id),
              tooltip: 'Edit Proposal',
              className: 'text-blue-600',
            },
            {
              icon: <FaFilePdf />,
              onClick: () => getproposalPdf(rowData.id),
              tooltip: 'Download PDF',
              className: `ml-4 ${
                rowData.state === 'PENDING' || rowData.state === 'SAVED'
                  ? 'text-gray-400'
                  : 'text-red-500'
              }`,
              disabled:
                rowData.state === 'PENDING' || rowData.state === 'SAVED',
            },
            {
              icon: <PiMicrosoftExcelLogoLight />,
              onClick: () => getExcelById(rowData.id),
              tooltip: 'Download Excel',
              className: `ml-4 ${
                rowData.state === 'PENDING' || rowData.state === 'SAVED'
                  ? 'text-gray-400'
                  : 'text-blue-700'
              }`,
            },
            {
              icon: <ImTable2 />,
              onClick: () => exportToPronto(rowData.id),
              tooltip: 'Download Export-to-pronto Contract',
              className: `ml-4 ${
                rowData.state === 'PENDING' || rowData.state === 'SAVED'
                  ? 'text-gray-400'
                  : 'text-blue-700'
              }`,
            },
            {
              icon: <FaTrash />,
              onClick: () => deleteQuote(rowData.id),
              tooltip: 'Delete Proposal',
              className: 'ml-4 text-red-500',
            },
          ]}
        />
      ),
    },
  ];

  const filteredData = proposalData.length
    ? proposalData.filter(
        (proposal) =>
          stateFilter?.length === 0 || stateFilter?.includes(proposal?.state),
      )
    : ['No Data Found'];

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  return (
    <Container title={'Proposal'}>
      <>
        <div className="flex justify-between items-center px-2">
          <StatusFilterBar
            stateFilter={stateFilter}
            setStateFilter={setStateFilter}
          />
          <SearchInput
            placeholder="Search Quote ..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
        {/* Add "WON" and "LOST" buttons */}
        <Table
          columns={columns}
          data={filteredData}
          totalElements={totalElements}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={(newPage) => setCurrentPage(newPage)}
          onSortChange={handleSortChange} // Pass sorting handler
          sortConfig={{ field: sortBy, direction: sortDir }} // Pass current sorting state
        />
        <div className="flex justify-start m-4">
          <button
            className="mr-2 bg-green-500 text-white py-2 px-4 rounded"
            onClick={handleMarkWon}
            disabled={selectedIds.length === 0}>
            WON
          </button>
          <button
            className="bg-red-500 text-white py-2 px-4 rounded"
            onClick={handleMarkLost}
            disabled={selectedIds.length === 0}>
            LOST
          </button>
        </div>
        <div>
          {showModal && (
            <Comment
              proposal={sampleProposal}
              onClose={() => setShowModal(false)}
              userName="Current User" // Pass the current user name here
            />
          )}
        </div>
      </>
    </Container>
  );
};

export default ProposalTable;
